import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { FormFolderContextMenuDirective } from '../menus/form-folder-context-menu.directive';
import { FormsPanelNavigationService } from '../services/forms-panel-navigation.service';
import { InputComponent } from '@frontendlib/components/controls/input/input.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { StitchTimestampsComponent } from '@modules/common/components/stitch-timestamps/stitch-timestamps.component';
import { TabsComponent } from '@modules/common/components/tabs/tabs.component';
import { type Tab } from '@modules/common/types/tab';
import { CommentsComponent } from '@modules/comments/components/comments/comments.component';
import { CommentsModule } from '@modules/comments/comments.module';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { DragDataTypes, type DragData } from '@modules/drag-n-drop/types/drag-data';
import { FormAttachmentsComponent } from './form-attachments.component';
import { ElementsModule } from '@modules/elements/elements.module';
import { LinkedInfoListComponent } from '@modules/elements/components/link-info/linked-info-list/linked-info-list.component';
import { StitchHubComponent } from '@modules/elements/components/stitch-hub/stitch-hub.component';
import { EntityHubSearchComponent } from '@modules/elements/components/entity-hub/entity-hub-search/entity-hub-search.component';
import { EditableTextareaComponent } from '@modules/form-controls/components/editable-textarea/editable-textarea.component';
import { FormControlsModule } from '@modules/form-controls/form-controls.module';
import { PermissionsService } from '@frontendlib/common/features/permissions/services/permissions.service';
import { type Actions, type Subjects, ViewSubjects } from '@sharedlib/common/permissions/types/Permissions';
import { validateNativeDragBatch } from '@sharedlib/common/boards/native-drag-payload';
import { StitchType } from '@sharedlib/common/types/StitchType';
import type { FormSummaryDto } from '@sharedlib/dtos/dto/forms-app/form.dto';
import type { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import { StitchItemOpenerService } from '@modules/linked-info/services/stitch-item-opener.service';
import { StitchService } from '@modules/common/services/stitch.service';
import { FormKnowledgeComponent } from './form-knowledge.component';
import { FormsSidebarActionsService } from '../services/forms-sidebar-actions.service';
import { FormsWorkspaceStore } from '../services/forms-workspace.store';
import { FormKnowledgeVisibilityService } from '../services/form-knowledge-visibility.service';
import { AccessLimitGateService } from '@modules/settings/services/access-limit-gate.service';
import { injectFullWindowStart } from '@modules/common/services/full-window.service';
import { Application } from '@modules/common/types/application';
import { APPLICATION_SCOPE_TOKEN, ApplicationScope } from '@modules/common/types/ApplicationScope';
import type { FormFolderSummaryDto } from '@sharedlib/dtos/dto/forms-app/form.dto';

export type FormFolderTab = 'forms' | 'details' | 'attachments' | 'stitch' | 'comments';
/** BoardsFolderPreview's native header, tabs, knowledge, attachments and leave-safe folder details. */
@Component({
  selector: 'app-forms-folder-record',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormFolderContextMenuDirective,
    NgTemplateOutlet,
    FormAttachmentsComponent,
    Button2Component,
    InputComponent,
    IconComponent,
    StitchTimestampsComponent,
    TabsComponent,
    CommentsModule,
    DragNDropModule,
    ElementsModule,
    LinkedInfoListComponent,
    StitchHubComponent,
    EntityHubSearchComponent,
    EditableTextareaComponent,
    FormControlsModule,
    FormKnowledgeComponent,
  ],
  templateUrl: './forms-folder-record.component.html',
  host: { class: 'relative flex h-full min-h-0 flex-col bg-white' },
})
export class FormsFolderRecordComponent {
  public readonly contents = input.required<TemplateRef<unknown>>();
  public readonly store = inject(FormsWorkspaceStore);
  public readonly tab = signal<FormFolderTab>('forms');
  protected readonly visited = signal(new Set<FormFolderTab>(['forms']));
  private readonly discussion = viewChild(CommentsComponent);
  public pendingWork(): { dirty: boolean; busy: boolean; commentDirty: boolean } {
    const comments = this.discussion()?.getPendingWork() || { dirty: false, busy: false };
    return { ...comments, commentDirty: comments.dirty, busy: comments.busy || this.store.folderActivity() };
  }
  protected readonly folder = this.store.selectedFolder;
  public readonly selectContents = output<void>();
  public readonly formCreated = output<FormFolderSummaryDto>();
  public readonly closeRecord = input<() => Promise<boolean>>();
  public async openFolderMenu(selectAll: boolean): Promise<void> { await this.selectTab('forms'); if (selectAll) this.selectContents.emit(); }
  protected readonly form = this.store.folderForm;
  protected readonly tabs: Tab<FormFolderTab>[] = [
    { title: 'Forms', value: 'forms' },
    { title: 'Details', value: 'details' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  private readonly scope = inject(APPLICATION_SCOPE_TOKEN, { optional: true });
  private readonly visibility = inject(FormKnowledgeVisibilityService);
  protected readonly displayKnowledge = computed(() => this.visibility.visible(this.scope));
  private readonly permissions: PermissionsService<Actions, Subjects> = inject(PermissionsService);
  protected readonly permissionsLoaded = this.permissions.hasPermissionsLoaded;
  protected readonly hasKnowledgeHubAccess = computed(() =>
    this.permissions.checkPermission('read', ViewSubjects.knowledgeHubView),
  );
  private readonly panel = inject(FormsPanelNavigationService);
  private readonly actions = inject(FormsSidebarActionsService);
  private readonly opener = inject(StitchItemOpenerService);
  private readonly injector = inject(Injector);
  private readonly destroy = inject(DestroyRef);
  private readonly stitch = inject(StitchService);
  private readonly startFullWindow = injectFullWindowStart(Application.forms);
  protected readonly canDrop = (data: DragData): boolean => {
    const folder = this.folder();
    const work = this.pendingWork();
    return (
      this.tab() !== 'stitch' &&
      !!folder &&
      !folder.deleted &&
      !folder.archived &&
      !this.store.busy() &&
      !this.store.dirty() &&
      !this.store.folderCopyPending() &&
      !work.dirty &&
      !work.busy &&
      data.type === DragDataTypes.form &&
      this.movableForms(data).length > 0
    );
  };
  public constructor() {
    this.destroy.onDestroy(this.store.registerRecordWork(() => this.pendingWork()));
    this.destroy.onDestroy(() => this.store.folderActivity.set(false));
  }
  private movableForms(data: DragData): FormSummaryDto[] {
    if (data.type !== DragDataTypes.form) return [];
    const folder = this.folder();
    if (!folder) return [];
    const items: readonly unknown[] = data.data;
    const rows = items.filter(
      (item): item is FormSummaryDto =>
        typeof item === 'object' &&
        item !== null &&
        'itemType' in item &&
        item.itemType === StitchType.FORM &&
        'id' in item &&
        typeof item.id === 'string' &&
        'accountId' in item &&
        item.accountId === folder.accountId &&
        'headVersion' in item &&
        typeof item.headVersion === 'number',
    );
    if (rows.length !== items.length || rows.length > 50) return [];
    return [
      ...new Map(
        rows
          .filter((item) => item.folderUid !== folder.id || item.archived || item.deleted)
          .map((item) => [item.id, item]),
      ).values(),
    ];
  }
  protected async drop(data: DragData): Promise<void> {
    if (!this.canDrop(data)) return;
    const folder = this.folder();
    if (!folder) return;
    const items = this.movableForms(data),
      generation = this.store.documentGeneration();
    this.store.busy.set(true);
    let failure: unknown;
    try {
      for (const item of items) {
        await this.store.api.patch(folder.accountId, item, { folderUid: folder.id, archived: false, deleted: false });
      }
    } catch (error) {
      failure = error;
    } finally {
      if (generation === this.store.documentGeneration()) {
        // A batch can partially succeed; refresh contents and keep the failure visible after the refresh.
        try {
          await this.store.reload();
        } catch (error) {
          failure ??= error;
        } finally {
          this.store.busy.set(false);
        }
        if (failure !== undefined) this.store.fail(failure, 'Could not save the folder. Your changes are kept; try again.');
      }
    }
  }
  protected readonly canStitch = (data: DragData): boolean => {
    const folder = this.folder();
    return (
      !!folder &&
      !folder.deleted &&
      !folder.archived &&
      !this.store.busy() &&
      !this.store.dirty() &&
      !this.store.folderCopyPending() &&
      !this.pendingWork().dirty &&
      !this.pendingWork().busy &&
      validateNativeDragBatch(data.data, { purpose: 'stitch', spaceId: folder.accountId, target: folder }).ok
    );
  };
  protected stitchDrop(data: DragData): void {
    const folder = this.folder();
    if (folder && this.canStitch(data)) this.stitch.linkDragData(folder, data);
  }
  public async selectTab(tab: FormFolderTab): Promise<void> {
    this.visited.update((value) => new Set([...value, tab]));
    this.tab.set(tab);
  }
  protected toggleKnowledge(): void {
    this.visibility.toggle(this.scope);
  }
  protected async createForm(): Promise<void> {
    const folder = this.folder();
    if (!folder || this.injector.get(AccessLimitGateService).blocks('createItem', this.injector)) return;
    await this.store.create(folder.id);
    if (this.scope === ApplicationScope.DYNAMIC_PANEL && this.store.current()) { this.store.editor.set(false); this.formCreated.emit(folder); }
    if (this.store.current() && this.store.editor()) this.startFullWindow?.();
  }
  protected save(): void {
    void this.store.save().catch((error) => this.store.fail(error, 'Could not save the folder. Your changes are kept; try again.'));
  }
  protected openPanel(): void {
    const folder = this.folder();
    if (folder && !this.store.dirty() && !this.store.busy() && !this.store.folderActivity())
      this.panel.open(folder.id, folder.accountId, 'folder');
  }
  protected archive(): void {
    const folder = this.folder()!;
    if (folder.archived) void this.store.folderMetadata({ archived: false });
    else this.actions.changeFolderState(folder, 'archive', changes => { if (this.store.selectedFolder()?.id === folder.id && this.store.selectedFolder()?.accountId === folder.accountId) void this.store.folderMetadata(changes); });
  }
  protected trash(): void {
    const folder = this.folder()!;
    if (folder.deleted) void this.store.folderMetadata({ deleted: false });
    else this.actions.changeFolderState(folder, 'trash', changes => { if (this.store.selectedFolder()?.id === folder.id && this.store.selectedFolder()?.accountId === folder.accountId) void this.store.folderMetadata(changes); });
  }
  protected close(): void {
    if (!this.store.canLeaveRecord()) return;
    void (this.closeRecord()?.() ?? this.store.close());
  }
  protected openItem(item: StitchItemSummaryDto): void {
    if (this.store.busy() || this.store.dirty() || this.store.folderActivity()) return;
    this.opener.openFullForm(item, this.injector, this.destroy);
  }
}
